import React, { useState, useEffect, useMemo } from 'react';
import {
	FingotiButton,
	FingotiDelete,
	FingotiModal,
	FingotiHeader,
	FingotiMarkdown,
	FingotiTable,
} from '@fingoti/components';
import { useFingotiSessions } from '@fingoti/auth-js/react';
import { useSnackbar } from 'notistack';

import makeStyles from '@material-ui/core/styles/makeStyles';

import SvgIcon from '@material-ui/core/SvgIcon';
import AppleIcon from '@material-ui/icons/Apple';
import AndroidOutlinedIcon from '@material-ui/icons/AndroidOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import PersonIcon from '@material-ui/icons/Person';

import { UserSessionInfo } from './UserSessionInfo';

import { datetimeService } from '../../services/datetime.service';
import { sessionService } from '../../services/session.service';

import { ReactComponent as FingotiLogo } from '../../icons/FingotiIcon.svg';
import { useTheme } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
	actionIcon: {
		padding: 0,
	},
	client: {
		display: 'flex',
		alignItems: 'center',
	},
	clientIcon: {
		marginRight: theme.spacing(2),
		fill: theme.palette.greyTwo.main,
	},
	currentSession: {
		background: theme.palette.primary.lighter,
	},
}));

const WindowsIcon = (props) => {
	return (
		<SvgIcon {...props}>
			<path d='M3,12V6.75L9,5.43V11.91L3,12M20,3V11.75L10,11.9V5.21L20,3M3,13L9,13.09V19.9L3,18.75V13M20,13.25V22L10,20.09V13.1L20,13.25Z' />
		</SvgIcon>
	);
};

const FingotiIcon = (props) => {
	return (
		<SvgIcon {...props}>
			<FingotiLogo />
		</SvgIcon>
	);
};

export const UserSessions = () => {
	const theme = useTheme();
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();
	const { sessions, isLoading, error, revoke, revokeOthers } =
		useFingotiSessions();
	const [infoSession, setInfoSession] = useState();
	const [infoOpen, setInfoOpen] = useState(false);

	// Mirror the pre-auth-js ordering: most recent login first, with the current
	// device pinned to the top. `isCurrent` replaces the old findAppToken()/sid
	// lookup, and `authTime` is the new field for the old `loginTime`.
	const orderedSessions = useMemo(() => {
		return [...sessions].sort((a, b) => {
			if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
			return new Date(b.authTime) - new Date(a.authTime);
		});
	}, [sessions]);

	// Surface a list-load failure once when it changes (revoke errors are handled
	// inline by their own handlers below).
	useEffect(() => {
		if (error) {
			enqueueSnackbar(error?.message ?? 'Failed to load sessions', {
				variant: 'error',
			});
		}
	}, [error, enqueueSnackbar]);

	const endSession = async (id) => {
		try {
			await revoke(id);
			enqueueSnackbar('Session ended', { variant: 'success' });
		} catch (err) {
			enqueueSnackbar(err?.message ?? 'Failed to end session', {
				variant: 'error',
			});
		}
	};

	const endAllSessions = async () => {
		try {
			await revokeOthers();
			enqueueSnackbar('All other sessions ended', { variant: 'success' });
		} catch (err) {
			enqueueSnackbar(err?.message ?? 'Failed to end sessions', {
				variant: 'error',
			});
		}
	};

	const handleInfoOpen = (session) => {
		setInfoSession(session);
		setInfoOpen(true);
	};

	const actionButtons = () => {
		return (
			<React.Fragment>
				<FingotiButton light color='primary' onClick={() => endAllSessions()}>
					Close other sessions
				</FingotiButton>
			</React.Fragment>
		);
	};

	const breadcrumbs = [
		{ text: 'User', link: '/user' },
		{ text: 'Sessions', link: '' },
	];

	const platformRenderer = (rd) => {
		let icon = <HelpOutlineOutlinedIcon className={classes.clientIcon} />;

		if (sessionService.isFingoti(rd)) {
			icon = <FingotiIcon className={classes.clientIcon} />;
		} else if (sessionService.isWindows(rd)) {
			icon = <WindowsIcon className={classes.clientIcon} />;
		} else if (sessionService.isAndroid(rd)) {
			icon = <AndroidOutlinedIcon className={classes.clientIcon} />;
		} else if (sessionService.isApple(rd)) {
			icon = <AppleIcon className={classes.clientIcon} />;
		}

		return (
			<div className={classes.client}>
				{icon} {sessionService.platformLabel(rd)}
			</div>
		);
	};

	const lastActiveRenderer = (rd) => {
		return rd.isCurrent
			? 'Active Now'
			: datetimeService.formatDateTimeNoSeconds(new Date(rd.lastSeenOn));
	};

	return (
		<React.Fragment>
			<FingotiModal
				title='Session Information'
				open={infoOpen}
				setOpen={setInfoOpen}>
				<UserSessionInfo session={infoSession} setOpen={setInfoOpen} />
			</FingotiModal>
			<FingotiHeader
				breadcrumbs={breadcrumbs}
				actionButtons={actionButtons()}
				sectionIcon={PersonIcon}
			/>
			<FingotiMarkdown path='/documentation/account/user/sessions.md' />
			<FingotiTable
				data={orderedSessions}
				isLoading={isLoading}
				options={{
					filtering: false,
					rowStyle: (rowData) => {
						return {
							backgroundColor:
								rowData.isCurrent && theme.palette.primary.lighter,
						};
					},
				}}
				columns={[
					{ title: 'id', field: 'id', hidden: true },
					{ title: 'platform', render: platformRenderer },
					{ title: 'client', field: 'clientId' },
					{ title: 'location', render: (rd) => sessionService.location(rd) },
					{ title: 'last active', render: lastActiveRenderer },
				]}
				actions={[
					(rowData) => ({
						icon: InfoOutlinedIcon,
						tooltip: 'session information',
						onClick: () => handleInfoOpen(rowData),
					}),
					(rowData) => ({
						icon: () =>
							!rowData.isCurrent ? (
								<FingotiDelete
									remove
									tooltipText='end session'
									className={classes.actionIcon}
									onClick={() => endSession(rowData.id)}
								/>
							) : (
								<></>
							),
					}),
				]}
			/>
		</React.Fragment>
	);
};
